/**
 * Drill-down modal for every PO Line Dashboard figure — the PO-line sibling of
 * the Scheduled Dashboard's ``CurrentWeekDrilldownModal``: same shell (title,
 * count line, Export to Excel), same category TAB BAR for segment groups (OTD
 * ring → On time / Early / Late; OTIF ring + tile → All / Passed / Pending /
 * Failed; day/week bars → On time / Early / Late / Pending) with the tile's
 * own figures in the tab labels, the same numbered pager, and the same
 * row-level OTIF decision actions (admins only, server-gated).
 *
 * Pages through ``/analytics/po-line-dashboard/drilldown/``; rows are built
 * from the tile's own predicates so the count equals the figure.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import {
  poLineDashboardAPI,
  type POLineDrilldownMode,
  type POLineDrilldownRow,
} from '../../../lib/api/po-line-dashboard';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { DrilldownPager } from './DrilldownPager';
import { POLineDrilldownTable } from './POLineDrilldownTable';
import { MODE_META, getTabGroup, type DrilldownTabContext, type POLineDrilldownFilters } from './poLineDrilldownModes';

export { LineOtifPill } from './POLineDrilldownTable';

const PAGE_SIZE = 50;

export interface POLineDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: POLineDrilldownMode;
  filters: POLineDrilldownFilters;
  /** ISO period start (inclusive). Omit for "all history" (cumulative arrears). */
  periodStart?: string;
  /** ISO period end (inclusive). */
  periodEnd?: string;
  /** Grain of the tile that opened the drill (W3-T7: a week shows no arrears stock). */
  periodGrain?: 'week' | 'month';
  /** Human label for the period, e.g. "This week", "Mar". */
  periodLabel?: string;
  /** ISO currency code every value column is in (from the dashboard payload). */
  currency: string;
  /**
   * Tile figures per mode — rendered in the sibling tab labels so the tab reads
   * "Passed (12)" with the exact number the ring showed. Omit for no counts.
   */
  tabCounts?: Partial<Record<POLineDrilldownMode, number>>;
  /** ``'bars'`` — day/week bar segments (On time / Early / Late / Pending);
   *  ``'otd_mix'`` — the OTD ring (no Pending). OTIF modes need no context. */
  tabContext?: DrilldownTabContext;
  /** Fired after a row-level OTIF decision so the dashboard can refresh its tiles. */
  onDataChanged?: () => void;
}

export function POLineDrilldownModal({
  open, onClose, mode, filters, periodStart, periodEnd, periodGrain, periodLabel, currency,
  tabCounts, tabContext, onDataChanged,
}: POLineDrilldownModalProps) {
  const { formatDate } = useFormatters();

  // The active category — starts at the clicked mode, switchable via the tab bar.
  const [activeMode, setActiveMode] = useState<POLineDrilldownMode>(mode);
  useEffect(() => { if (open) setActiveMode(mode); }, [open, mode]);
  const meta = MODE_META[activeMode];
  const tabGroup = useMemo(() => getTabGroup(mode, tabContext), [mode, tabContext]);

  const [rows, setRows] = useState<POLineDrilldownRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [ordering, setOrdering] = useState<string>(meta.defaultOrdering);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Bumped after an OTIF decision so the current page re-reads its rows.
  const [refreshTick, setRefreshTick] = useState(0);

  // Reset paging/sort whenever the figure behind the modal changes.
  useEffect(() => {
    setPage(1);
    setOrdering(MODE_META[activeMode].defaultOrdering);
  }, [activeMode, periodStart, periodEnd, filters.customer_id, filters.group, filters.company, filters.site, filters.supplier_id, filters.part_number]);

  const baseParams = useMemo(() => ({
    mode: activeMode,
    customer_id: filters.customer_id,
    group: filters.group,
    company: filters.company,
    site: filters.site,
    supplier_id: filters.supplier_id,
    part_number: filters.part_number,
    start: periodStart,
    end: periodEnd,
    grain: periodGrain,
    ordering,
  }), [activeMode, filters.customer_id, filters.group, filters.company, filters.site, filters.supplier_id, filters.part_number, periodStart, periodEnd, periodGrain, ordering]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    poLineDashboardAPI
      .drilldown({ ...baseParams, page, page_size: PAGE_SIZE })
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.data.results);
        setTotal(resp.data.count);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('PO line drilldown failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load rows');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, baseParams, page, refreshTick]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleHeaderClick = useCallback((sortKey: string | null) => {
    if (!sortKey) return;
    setPage(1);
    setOrdering((cur) => (cur === sortKey ? `-${sortKey}` : sortKey));
  }, []);

  // After an override / acknowledge / reset: re-read this page (the row's pill
  // and the "All/Passed/Failed" membership change) and tell the dashboard to
  // refresh its tiles so the ring and the tab counts move together.
  const handleOtifDecision = useCallback(() => {
    setRefreshTick((t) => t + 1);
    onDataChanged?.();
  }, [onDataChanged]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await poLineDashboardAPI.drilldownExport(baseParams);
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const window = periodStart && periodEnd ? `_${periodStart}_${periodEnd}` : '';
      a.download = `po_line_dashboard_${activeMode}${window}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('PO line drilldown export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [baseParams, activeMode, periodStart, periodEnd]);

  const description = useMemo(() => {
    const parts = [meta.description];
    if (periodLabel) parts.push(periodLabel);
    else if (periodStart && periodEnd) parts.push(`${formatDate(periodStart)} – ${formatDate(periodEnd)}`);
    else if (periodEnd) parts.push(`up to ${formatDate(periodEnd)}`);
    if (filters.part_number) parts.push(`part ${filters.part_number}`);
    if (filters.supplier_id) parts.push(`supplier #${filters.supplier_id}`);
    parts.push(`${total.toLocaleString()} row${total === 1 ? '' : 's'}`);
    return parts.join(' • ');
  }, [meta.description, periodLabel, periodStart, periodEnd, filters.part_number, filters.supplier_id, total, formatDate]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="!max-w-[min(1480px,95vw)] w-[min(1480px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{meta.title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {/* Category tabs — only for segment groups (bars / OTD ring / OTIF ring + tile). */}
        {tabGroup && (
          <div className="flex flex-wrap gap-1 border-b pb-2" role="tablist" aria-label="Drill-down categories">
            {tabGroup.map((t) => {
              const active = t.mode === activeMode;
              const count = tabCounts?.[t.mode];
              return (
                <button
                  key={t.mode}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveMode(t.mode)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t.label}{count !== undefined ? <span className="ml-1 tabular-nums opacity-80">({count.toLocaleString()})</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : total === 0 && !loading ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No rows match this figure.</div>
        ) : (
          <POLineDrilldownTable
            entity={meta.entity}
            rows={rows}
            ordering={ordering}
            onHeaderClick={handleHeaderClick}
            currency={currency}
            onOtifDecision={handleOtifDecision}
          />
        )}

        {total > PAGE_SIZE && (
          <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} />
        )}
      </DialogContent>
    </Dialog>
  );
}
